import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import T from 'i18n-react';
import { get as lodashGet, camelCase as lodashCamelCase } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { actions as formActions } from 'react-redux-form';
import { v4 as getUUID } from 'uuid';
import {
  DatePicker,
  TextFieldCore,
  Dropzone,
  SelectDropdown,
  StickyScrollableContainer,
  StatusColorIndicatorOption,
  WithScrollableContainerRef,
  Loader,
} from '@adsk/bim360-matrix-react-components';
import { connect } from '../../redux/connect';
import {
  createIssueAction,
  createVirtualFolderAction,
  createAttachmentForCreate,
  cancelAttachmentUpload,
  trackEvent,
  clearUploadingAttachmentsListByOrigin,
  getRfiCustomIdentifierAction,
} from '../../redux/actions';
import {
  getCurrentUser,
  getOxygenMappings,
  getPendingIssue,
  getPendingIssueForm,
  getCurrentSheets,
  getActiveUsers,
  getGroupedProjectActiveEntities,
  getUploadingAttachments,
  isUploadingAttachments,
  getCurrentViewType,
  getPendingIssueFormAttributes,
  getIsCreateFormDeepLinking,
  rfiCustomIdentifierAnalyticsData,
  rfiSpecSection,
  getImpacts,
  getRFIEnum,
  getProjectId,
} from '../../redux/selectors';
import AttachmentUploadThunk from '../../redux/thunk/attachmentUploadThunk';
import { toOptionsArray } from '../../utils/parsing-helpers';
import {
  documentType,
  oxygenType,
  userType,
  activePushpinAttributesType,
  pendingIssueAttributesType,
  pendingIssueFormAttributesType,
  sheetObjectType,
} from '../../constants/proptype-validation';
import {
  STATUS_DRAFT,
  STATUS_OPEN,
  STATUS_SUBMITTED,
  statusColorMapping,
} from '../../constants/status-types';
import {
  ISSUE_ATTRIBUTE_NAMES,
  FORM_CONFIG_NAMES,
  FORM_CONFIG,
} from '../../constants/issue-attribute-names';
import Oxygen from '../../services/oxygen';
import IssueCreateFormAttachmentsSection from './components/IssueCreateFormAttachmentsSection';
import { getMandatoryFields, presenceValidator } from '../../utils/validation-helpers';
import { getUserCompanyName } from '../../utils/userHelpers';
import { getUsersList } from '../../utils/assigned-to-helpers';
import { createIssue } from '../../utils/analytics-helpers';
import { getAllowedFiles } from '../../utils/attachmentsHelper';
import IssuePreviewNoWorkflowConfiguration from '../IssuePreviewNoWorkflowConfiguration';
import AnimateHeightPopUp from '../AnimateHeightPopUp';
import LinkToDocument from './components/LinkToDocument';
import { viewTypes } from '../../constants/view-types';
import {
  CREATE_FORM_FIELD_NAMES_TO_KEEP_ON_CREATE_ANOTHER,
  CREATE_FORM_SEPARATOR,
} from '../../constants/create-form';
import { OSS_URN_TYPE, DM_URN_TYPE, attachmentOrigin } from '../../constants/attachment-types';
import FilterSearchIcon from '../FilterSearchIcon';
import FormField, { withFormField } from '../FormField';
import RfiCustomIdentifier from '../RfiCustomIdentifier';
import FormSection from './components/FormSection';
import { getStatusFromServerStatus } from '../../utils/transition-helpers';
import { RfiEnums } from '../../constants/rfi-enums';
import DocsSDKWrapper from '../../services/docsSDKWrapper';
import featureFlags, { FEATURE_FLAGS } from '../../utils/featureFlags';

const docsSDKWrapper = DocsSDKWrapper();

const scrollableContainerClassName = 'IssueCreateForm__form-content-container';
const SelectDropdownWithContainer = WithScrollableContainerRef(
  SelectDropdown,
  scrollableContainerClassName
);
const DatePickerWithContainer = WithScrollableContainerRef(
  DatePicker,
  scrollableContainerClassName
);

class IssueCreateForm extends PureComponent {
  constructor(props) {
    super(props);
    this.state = this.resetState(props);
    this.fieldComponents = {};
    this._resetAndKeepPreviousNeededValues = this._resetAndKeepPreviousNeededValues.bind(this);
    this.resetState = this.resetState.bind(this);
    this.handleScrollableRef = this.handleScrollableRef.bind(this);
    this.getFieldErrorMessage = this.getFieldErrorMessage.bind(this);
    this.changeFormValue = this.changeFormValue.bind(this);
    this.changeRfiCustomIdentifierFormValue = this.changeRfiCustomIdentifierFormValue.bind(this);
    this.changeRfiCustomIdentifierFormValidationValue = this.changeRfiCustomIdentifierFormValidationValue.bind(
      this
    );
    this.setFieldRef = this.setFieldRef.bind(this);
    this.scrollToInvalidField = this.scrollToInvalidField.bind(this);
    this.requiredFieldMessage = T.translate('new.fieldRequired');
    this.uploadFiles = this.uploadFiles.bind(this);
    this.chooseDocsDocument = this.chooseDocsDocument.bind(this);
    this.onFileCancelClick = this.onFileCancelClick.bind(this);
    this.handleSelectDocumentPushpin = this.handleSelectDocumentPushpin.bind(this);
    this.handleRemoveDocumentPushpin = this.handleRemoveDocumentPushpin.bind(this);
    this.renderCreateComponent = this.renderCreateComponent.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.renderAttributesGroups = this.renderAttributesGroups.bind(this);
    this.applyInitialFieldsData = this.applyInitialFieldsData.bind(this);
    this.isUseVirtualFolderEnabled = featureFlags.get(
      FEATURE_FLAGS.RFIS_USE_THE_RFI_VIRTUAL_FOLDER_URN
    );
  }

  componentDidMount() {
    if (!this.props.currentUser.loading) {
      this.setIssueDropdownDefaultValues();
    }
    if (this.isUseVirtualFolderEnabled) {
      this.populateRfiVirtualFolder();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.issuePushpin !== nextProps.issuePushpin) {
      this.setState({ issuePushpin: nextProps.issuePushpin });
    }
    if (this.props.currentSheet !== nextProps.currentSheet) {
      this.setState({ currentSheet: nextProps.currentSheet });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.titleInput && this.props.focusForm && !prevProps.focusForm) {
      this.titleInput.focus();
    }
    if (prevProps.currentUser.loading && !this.props.currentUser.loading) {
      this.setIssueDropdownDefaultValues();
    }
  }

  componentWillUnmount() {
    this.props.clearUploadingAttachmentsListByOrigin(attachmentOrigin.CREATE_FORM);
  }

  resetState(props) {
    return {
      selectedTransition: null,
      showNoWorkflowConfiguration: false,
      prevTransition: null,
      document: props.document,
      issuePushpin: props.issuePushpin,
      currentSheet: props.currentSheet,
      targetUrn: props.targetUrn,
      // TODO: remove this flag once the BE will return it in users/me as permitted field for open status
      // https://jira.autodesk.com/browse/BTLV-11822
      isRfiCustomIdentifierVisible: false,
      forceRfiCustomIdentifierValidation: false,
      populateRfiCustomIdentifierOnMount: true,
      formReady: false,
      openFormTime: Date.now(),
      id: null,
      virtualFolderUrn: null,
    };
  }

  handleScrollableRef(node) {
    this.scrollableElement = node;
  }

  getStatusOptions() {
    const { currentUser, oxygen } = this.props;
    const rfisNew = lodashGet(currentUser, 'attributes.rfis.new');

    let options = [];

    if (rfisNew) {
      const createTransitionsOptions = (transitionData, transitionsOptions = []) => {
        const {
          status,
          serverStatus = status, // in V1 serverStatus is undefined so set status as default for V1 compatibility
          assignees,
          id: transitionId = null,
        } = transitionData;
        const {
          transitionsFlow: currentTransitionsFlow = [],
          requiredAttributes: currentAdditionalRequiredAttributes = [],
        } = transitionsOptions.length ? transitionsOptions[transitionsOptions.length - 1] : {};
        const transitionsFlow = currentTransitionsFlow.concat(transitionId ? [transitionId] : []);
        const assigneeUsers = getUsersList(assignees, oxygen);
        const additionalRequiredAttributes = currentAdditionalRequiredAttributes
          .concat(transitionData.required_attributes || [])
          .map(lodashCamelCase);
        transitionsOptions.push({
          value: transitionId || status,
          label: T.translate(`status.${status}`),
          status,
          serverStatus,
          transitionId,
          transitionsFlow,
          additionalRequiredAttributes,
          assignees,
          assigneeUsers,
        });
        if (transitionData.transitions) {
          transitionData.transitions.forEach((subTransitionData) => {
            transitionsOptions = createTransitionsOptions(subTransitionData, transitionsOptions);
          });
        }
        return transitionsOptions;
      };
      rfisNew.initialData.forEach((initialDataState) => {
        options = createTransitionsOptions(initialDataState, options);
      });
    }

    return options;
  }

  handleSelectStatusTransition(selectedTransition, populateRfiCustomIdentifierOnMount = true) {
    const { setErrorsFormAction, pendingIssue } = this.props;

    if (isEmpty(selectedTransition.assigneeUsers)) {
      const prevTransition = this.state.selectedTransition;
      this.setState({ showNoWorkflowConfiguration: true, prevTransition });
    }
    const currentSelectedTransition = this.state.selectedTransition;
    const prevTransitionRequiredAttributes = getMandatoryFields(currentSelectedTransition);
    const newTransitionRequiredAttributes = getMandatoryFields(selectedTransition);
    const noLongerRequiredAttributes = prevTransitionRequiredAttributes.filter(
      (attrName) => !newTransitionRequiredAttributes.includes(attrName)
    );
    const newRequiredAttributes = newTransitionRequiredAttributes.filter(
      (attrName) => !prevTransitionRequiredAttributes.includes(attrName)
    );
    noLongerRequiredAttributes.forEach((attrName) => {
      setErrorsFormAction(`pendingIssue.${attrName}`, { submitFailed: null });
    });
    newRequiredAttributes.forEach((attrName) => {
      setErrorsFormAction(`pendingIssue.${attrName}`, {
        submitFailed: !presenceValidator(pendingIssue[attrName]) && this.requiredFieldMessage,
      });
    });

    this.setState({ selectedTransition });

    const isRfiCustomIdentifierVisible = selectedTransition.status === STATUS_OPEN;

    // if user selected a status different from Open - we should reset submitFailed since RfiCustomIdentifier is not rendered in the form
    if (!isRfiCustomIdentifierVisible) {
      setErrorsFormAction(`pendingIssue['${ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER}']`, {
        submitFailed: null,
      });
    }

    // save the field visibility on the state
    this.setState({ isRfiCustomIdentifierVisible, populateRfiCustomIdentifierOnMount });

    this.props.changeFormValueAction('pendingIssue.status', selectedTransition.serverStatus);
    this.props.setErrorsFormAction(
      'pendingIssue.status',
      !presenceValidator(selectedTransition.serverStatus)
    );
  }

  changeFormValue(value, fieldName) {
    const { changeFormValueAction, setErrorsFormAction } = this.props;
    let submitFailed;

    changeFormValueAction(`pendingIssue['${fieldName}']`, value);
    if (this.isFieldMandatory(fieldName)) {
      submitFailed = !presenceValidator(value) && this.requiredFieldMessage;
    }

    setErrorsFormAction(`pendingIssue['${fieldName}']`, { submitFailed });
  }

  changeRfiCustomIdentifierFormValue(value) {
    const { changeFormValueAction } = this.props;
    changeFormValueAction(`pendingIssue['${ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER}']`, value);
  }

  changeRfiCustomIdentifierFormValidationValue(isValid) {
    const { setErrorsFormAction } = this.props;
    const submitFailed = !isValid ? true : null;
    setErrorsFormAction(`pendingIssue['${ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER}']`, {
      submitFailed,
    });
  }

  isFieldMandatory(fieldName) {
    const { selectedTransition } = this.state;
    return getMandatoryFields(selectedTransition).includes(fieldName);
  }

  handleSelectAssignee(event) {
    const { changeFormValueAction, setErrorsFormAction, oxygen } = this.props;
    const oxygenHelper = new Oxygen();
    oxygenHelper.setMappings(oxygen);

    changeFormValueAction('pendingIssue.assignedTo', event.oxygenId);

    setErrorsFormAction('pendingIssue.assignedTo', {
      submitFailed:
        this.isFieldMandatory(ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO) &&
        !presenceValidator(event.oxygenId) &&
        this.requiredFieldMessage,
    });
  }

  uploadFiles(filesArray) {
    const { virtualFolderUrn } = this.state;

    this.props.uploadAttachmentFiles(
      [...filesArray].map((file) => {
        return { id: getUUID(), urnType: OSS_URN_TYPE, name: file.name, file };
      }),
      null,
      false,
      attachmentOrigin.CREATE_FORM,
      virtualFolderUrn
    );
  }

  chooseDocsDocument(documentUrn, document) {
    this.props.createAttachmentForCreate({
      document,
      fileId: documentUrn,
      id: documentUrn,
      urn: documentUrn,
      name: document.name || lodashGet(document, 'currentVersion.name'),
      urnType: DM_URN_TYPE,
      origin: attachmentOrigin.CREATE_FORM,
    });
  }

  onFileCancelClick(file) {
    this.props.cancelAttachmentUpload(file.id);
  }

  getFieldErrorMessage(fieldName) {
    return (
      lodashGet(this.props, `pendingIssueFormAttributes['${fieldName}'].errors.touched`) ||
      lodashGet(this.props, `pendingIssueFormAttributes['${fieldName}'].errors.submitFailed`) ||
      undefined
    );
  }

  setIssueDropdownDefaultValues() {
    const { setErrorsFormAction } = this.props;
    const statusOptions = this.getStatusOptions();
    const submittedOption = statusOptions.find((status) => status.status === STATUS_SUBMITTED);
    const openOption = statusOptions.find((status) => status.status === STATUS_OPEN);
    const draftOption = statusOptions.find((status) => status.status === STATUS_DRAFT);

    let selectedTransition;

    // Default transition order is submitted, open, draft.
    // Verify there are available assignees.
    if (submittedOption && submittedOption.assigneeUsers.length) {
      selectedTransition = submittedOption;
    } else if (openOption && openOption.assigneeUsers.length) {
      selectedTransition = openOption;
    } else {
      selectedTransition = draftOption;
    }

    this.handleSelectStatusTransition(selectedTransition);

    const predefinedFieldNames = [
      ISSUE_ATTRIBUTE_NAMES.STATUS,
      ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER,
    ];

    getMandatoryFields(selectedTransition).forEach((fieldName) => {
      if (!predefinedFieldNames.includes(fieldName)) {
        setErrorsFormAction(`pendingIssue.${fieldName}`, {
          submitFailed: this.requiredFieldMessage,
        });
      }
    });

    this.setState({ formReady: true }, this.applyInitialFieldsData);
  }

  getIssueAttributes() {
    const { pendingIssue, uploadingAttachments } = this.props;
    const { document, issuePushpin, targetUrn, currentSheet, id, virtualFolderUrn } = this.state;
    const sendVirtualFolder = this.isUseVirtualFolderEnabled && virtualFolderUrn && id;

    const {
      title,
      description,
      suggestedAnswer,
      locationDescription,
      dueDate,
      coReviewers,
      distributionList,
      customIdentifier,
      costImpact,
      scheduleImpact,
      priority,
      discipline,
      category,
      reference,
      status,
      assignedTo,
    } = pendingIssue;
    const issueAttributes = {
      ...(sendVirtualFolder && { virtualFolderUrn, id }),
      title,
      description,
      suggestedAnswer,
      locationDescription,
      dueDate,
      costImpact,
      scheduleImpact,
      priority,
      discipline,
      category,
      reference,
    };

    Object.assign(issueAttributes, { status, assignedTo });

    if (!isEmpty(coReviewers)) {
      Object.assign(issueAttributes, { coReviewers: coReviewers.map((user) => user.oxygenId) });
    }
    if (!isEmpty(distributionList)) {
      Object.assign(issueAttributes, {
        distributionList: distributionList.map((user) => user.oxygenId),
      });
    }

    if (issuePushpin) {
      issueAttributes.pushpinAttributes = {
        type: issuePushpin.position.z ? 'TwoDVectorPushpin' : 'TwoDRasterPushpin',
        location: issuePushpin.position,
        objectId: issuePushpin.objectId ? issuePushpin.objectId.toString() : undefined,
        viewerState: issuePushpin.viewerState ? issuePushpin.viewerState : undefined,
        externalId: issuePushpin.externalId ? issuePushpin.externalId : undefined,
        attributesVersion: issuePushpin.attributesVersion
          ? issuePushpin.attributesVersion
          : undefined,
      };
    }
    if (document) {
      issueAttributes.startingVersion = document.version || document.latestVersion;
    }
    if (targetUrn) {
      issueAttributes.targetUrn = targetUrn;
    }
    if (currentSheet && currentSheet.sheetGuid) {
      issueAttributes.sheetMetadata = currentSheet;
    }
    // We have multiple target urn's, we need to decide on which of the urn's we have to create the issue on
    // We do this, by using the urn of the object that got selected on screen
    if (Array.isArray(issueAttributes.targetUrn) && issuePushpin && issuePushpin.objectData) {
      const decodeUrn = (urn) => {
        urn = urn.replace(/-/g, '+');
        urn = urn.replace(/_/g, '/');
        while (urn.length % 4) {
          urn += '=';
        }
        return atob(urn);
      };
      // Read the urn from the object that got selected on screen
      const decodedUrn = decodeUrn(issuePushpin.objectData.urn);
      const lineageUrn = decodedUrn.match(/\.(?!.*\.).*\?/);
      if (lineageUrn) {
        // Find the lineage in the list of target urn's of the issues UI which matches the urn of the selected object
        const urn = issueAttributes.targetUrn.find((x) =>
          x.contains(lineageUrn[0].substr(1, lineageUrn[0].length - 2))
        );
        // Could be not there, if you have other content in LMV than in issues UI: create it on the first URN we have
        if (urn) {
          issueAttributes.targetUrn = urn;
          // Read and write the version of the selected object
          const versionNumber = decodedUrn.match(/\d*$/);
          if (versionNumber) {
            issueAttributes.startingVersion = Number(versionNumber[0]);
          }
        }
      }
      issueAttributes.sheetMetadata = {
        sheetGuid: issuePushpin.objectData.guid,
        sheetName: issuePushpin.objectData.viewName,
      };
    }
    if (!isEmpty(uploadingAttachments)) {
      issueAttributes.attachmentsAttributes = uploadingAttachments
        .filter((attachment) => !attachment.isFailed)
        .reverse()
        .map(({ name, urn, urnType }) => ({ name, urn, urn_type: urnType }));
    }
    if (this.state.isRfiCustomIdentifierVisible) {
      Object.assign(issueAttributes, { reserveCustomIdentifier: customIdentifier.trim() });
    }
    return issueAttributes;
  }

  populateRfiVirtualFolder() {
    const { createVirtualFolder } = this.props;
    new Promise((resolve, reject) => {
      createVirtualFolder(resolve, reject);
    }).then(({ rfiVirtualFolderUrn, rfiId }) => {
      this.setState({
        virtualFolderUrn: rfiVirtualFolderUrn,
        id: rfiId,
      });
    });
  }

  _resetAndKeepPreviousNeededValues() {
    const savedState = {};
    CREATE_FORM_FIELD_NAMES_TO_KEEP_ON_CREATE_ANOTHER.forEach((fieldName) => {
      savedState[fieldName] = this.props.pendingIssue[fieldName];
    });

    const statusOptions = this.getStatusOptions();
    const selectedTransition = statusOptions.find(
      (status) => status.status === getStatusFromServerStatus(this.props.pendingIssue.status)
    );
    const savedStateFieldNames = Object.keys(savedState);

    this.setState({...this.resetState(this.props), formReady: true});

    this.props.resetFormAction('pendingIssue');
    savedStateFieldNames.forEach((key) => {
      if (key === ISSUE_ATTRIBUTE_NAMES.STATUS) {
        this.handleSelectStatusTransition(selectedTransition);
      } else {
        this.props.changeFormValueAction(`pendingIssue.${key}`, savedState[key]);
        this.props.setErrorsFormAction(`pendingIssue.${key}`, { submitFailed: null });
      }
    });

    getMandatoryFields(selectedTransition).forEach((fieldName) => {
      if (!savedStateFieldNames.includes(fieldName)) {
        this.props.setErrorsFormAction(`pendingIssue.${fieldName}`, {
          submitFailed: this.requiredFieldMessage,
        });
      }
    });
    this.props.clearUploadingAttachmentsListByOrigin(attachmentOrigin.CREATE_FORM);
    if (this.scrollableElement) {
      this.scrollableElement.scrollTop = 0;
    }
  }

  saveIssue() {
    const isFormCanBeSaved =
      this.props.pendingIssueForm.valid && !this.props.isUploadingAttachments;
    if (isFormCanBeSaved) {
      const {
        onSaveStart,
        onSaveSuccess,
        onSaveError,
        pendingIssue,
        trackEvent,
        origin,
        uploadingAttachments,
        currentUser,
        clearUploadingAttachmentsListByOrigin,
      } = this.props;
      const { assignedTo, status, customIdentifier } = pendingIssue;
      const { document, issuePushpin } = this.state;

      const extra = {};

      const { selectedTransition } = this.state;
      if (selectedTransition) {
        const transitionAttributes = {};
        if (assignedTo) {
          Object.assign(transitionAttributes, { assignedTo });
        }
        if (this.state.isRfiCustomIdentifierVisible) {
          Object.assign(transitionAttributes, { customIdentifier: customIdentifier.trim() });
        }
        Object.assign(extra, {
          transitionsFlow: selectedTransition.transitionsFlow,
          transitionAttributes,
        });
      }

      extra.onSuccess = (rfi) => {
        const { openFormTime } = this.state;
        if (this.props.createAnother) {
          this._resetAndKeepPreviousNeededValues();
        }
        if (onSaveSuccess) {
          onSaveSuccess(rfi);
        }
        clearUploadingAttachmentsListByOrigin(attachmentOrigin.CREATE_FORM);

        // MixPanel Event - when user create new issue
        const coReviewersCount = (rfi.coReviewers || []).length;
        const distributionMemberCount = (rfi.distributionList || []).length;
        const workflowType = lodashGet(currentUser, 'attributes.rfis.workflowType');
        const rfiCustomIdentifierAnalyticsData = this.state.isRfiCustomIdentifierVisible
          ? this.props.rfiCustomIdentifierAnalyticsData
          : null;
        const timeToCreateRFI = Math.round((Date.now() - openFormTime) / 1000);
        trackEvent(
          createIssue(
            coReviewersCount,
            distributionMemberCount,
            document,
            status,
            origin,
            issuePushpin,
            uploadingAttachments,
            workflowType,
            this.props.createAnother,
            rfiCustomIdentifierAnalyticsData,
            timeToCreateRFI,
            this.props.pendingIssue
          )
        );
      };
      if (onSaveError) {
        extra.onError = onSaveError;
      }
      if (onSaveStart) {
        onSaveStart();
      }
      extra.isDeepLinking = this.props.isCreateFormDeepLinking;
      const issueAttributes = this.getIssueAttributes();
      this.props.createIssueAction(issueAttributes, extra);
    } else {
      this.props.setSubmitFailedFormAction('pendingIssue', true);
      const isRfiCustomIdentifierInValid = lodashGet(
        this.props,
        `pendingIssueFormAttributes['${ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER}'].errors.submitFailed`
      );
      this.setState({ forceRfiCustomIdentifierValidation: isRfiCustomIdentifierInValid });

      this.scrollToInvalidField();
    }
  }

  /**
   * Scroll to first field with an error
   */
  scrollToInvalidField() {
    const firstErrorFieldName = Object.keys(this.props.pendingIssueFormAttributes).find(
      (key) => !this.props.pendingIssueFormAttributes[key].valid
    );
    if (firstErrorFieldName) {
      this.fieldComponents[firstErrorFieldName].scrollIntoView();
    }
  }

  handleSubmit(e) {
    e.preventDefault();
    this.saveIssue();
  }

  getAssignToOptions() {
    const { selectedTransition } = this.state;

    return lodashGet(selectedTransition, 'assigneeUsers', []);
  }

  shouldShowFieldError(fieldName) {
    // SubmitFailed - refers to errors that should be shown only after failing to submit form (missing required fields for example).
    // Touched - refers to a field which the user inserted value with errors (for example - too long description).
    const submitFailedCheck =
      lodashGet(this.props, `pendingIssueFormAttributes['${fieldName}'].submitFailed`) &&
      lodashGet(this.props, `pendingIssueFormAttributes['${fieldName}'].errors.submitFailed`);
    const touchedCheck = lodashGet(
      this.props,
      `pendingIssueFormAttributes['${fieldName}'].errors.touched`
    );
    return !!submitFailedCheck || touchedCheck;
  }

  /**
   * Save fields references
   * @param fieldName
   * @returns {function(*)}
   */
  setFieldRef(fieldName) {
    return (node) => {
      this.fieldComponents[fieldName] = node;
    };
  }

  renderAssignTo() {
    const { pendingIssue, currentUser, disabled } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO;
    const textLabelAssignedTo = T.translate('new.assignedTo');
    const showErrorMessage = this.shouldShowFieldError(fieldName);
    const errorMessage = this.getFieldErrorMessage(fieldName);

    if (pendingIssue.status === STATUS_DRAFT) {
      // When creating RFI in draft status - assignee should be the creator.
      const currentUserName = lodashGet(currentUser, 'attributes.displayName', '');
      return (
        <FormField
          key={fieldName}
          setFieldRef={this.setFieldRef(fieldName)}
          errorMessageText={errorMessage}
          labelText={textLabelAssignedTo}
          showFieldError={showErrorMessage}
          isFieldMandatory={this.isFieldMandatory(fieldName)}
        >
          <TextFieldCore
            className="IssueCreateForm__text-input IssueCreateForm__field-width"
            behavior={TextFieldCore.Behaviors.SINGLE_LINE}
            value={currentUserName}
            valid={!showErrorMessage}
            disabled
          />
        </FormField>
      );
    }
    const currentUserLoading = this.getIsCurrentUserLoading();
    const textPlaceholderAssignedTo = T.translate('new.placeholder.assignedToUsers');
    const assignedToOptions = this.getAssignToOptions();

    return (
      <FormField
        key={fieldName}
        setFieldRef={this.setFieldRef(fieldName)}
        errorMessageText={errorMessage}
        data-testid="IssueCreateForm__assignedTo"
        labelText={textLabelAssignedTo}
        showFieldError={showErrorMessage}
        isFieldMandatory={this.isFieldMandatory(fieldName)}
      >
        <SelectDropdownWithContainer
          options={assignedToOptions}
          selectedOptions={assignedToOptions.find(
            (option) => option.id === pendingIssue.assignedTo
          )}
          valid={!showErrorMessage}
          labelKey="name"
          valueKey="oxygenId"
          subtextGetter={getUserCompanyName}
          placeholder={textPlaceholderAssignedTo}
          className="IssueCreateForm__field-width"
          disabled={disabled || currentUserLoading}
          onChange={(value) => this.handleSelectAssignee(value)}
          searchable
          searchIcon={<FilterSearchIcon />}
          searchPlaceholder={T.translate('new.search')}
        />
      </FormField>
    );
  }

  handleSelectDocumentPushpin(document, issuePushpin, currentSheet) {
    this.setState({ document, issuePushpin, targetUrn: document.urn, currentSheet });
  }

  handleRemoveDocumentPushpin() {
    this.setState({ document: null, issuePushpin: null, targetUrn: null, currentSheet: null });
  }

  renderStatus() {
    const { pendingIssue, disabled } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.STATUS;

    const textLabelStatus = T.translate('new.status');
    const statusOptions = this.getStatusOptions();
    const currentUserLoading = this.getIsCurrentUserLoading();
    const showErrorMessage = this.shouldShowFieldError(fieldName);
    const errorMessage = this.getFieldErrorMessage(fieldName);

    return (
      <FormField
        key={fieldName}
        setFieldRef={this.setFieldRef(fieldName)}
        errorMessageText={errorMessage}
        data-testid="IssueCreateForm__status"
        labelText={textLabelStatus}
        showFieldError={showErrorMessage}
        isFieldMandatory={this.isFieldMandatory(fieldName)}
      >
        <SelectDropdownWithContainer
          selectedOptions={statusOptions.find(
            (option) => option.status === getStatusFromServerStatus(pendingIssue.status)
          )}
          options={statusOptions}
          valid={!showErrorMessage}
          optionRenderer={(value, label) => (
            <StatusColorIndicatorOption
              statusColor={
                statusColorMapping[statusOptions.find((option) => option.value === value).status]
              }
              label={label}
            />
          )}
          valueRenderer={(value, label) => (
            <StatusColorIndicatorOption
              disabled={disabled || currentUserLoading}
              statusColor={
                statusColorMapping[statusOptions.find((option) => option.value === value).status]
              }
              label={label}
            />
          )}
          className="IssueCreateForm__field-width"
          disabled={disabled || currentUserLoading}
          onChange={(status) =>
            this.handleSelectStatusTransition(status, status.status === STATUS_OPEN)}
          clearable={false}
        />
      </FormField>
    );
  }

  renderTitle() {
    const { pendingIssue, disabled } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.TITLE;

    const currentUserLoading = this.getIsCurrentUserLoading();
    const textLabelTitle = T.translate('new.title');
    const showErrorMessage = this.shouldShowFieldError(fieldName);
    const errorMessage = this.getFieldErrorMessage(fieldName);

    return (
      <FormField
        key={fieldName}
        setFieldRef={this.setFieldRef(fieldName)}
        errorMessageText={errorMessage}
        labelText={textLabelTitle}
        showFieldError={showErrorMessage}
        isFieldMandatory={this.isFieldMandatory(fieldName)}
      >
        <TextFieldCore
          ref={(input) => {
            this.titleInput = input;
          }}
          valid={!showErrorMessage}
          behavior={TextFieldCore.Behaviors.SINGLE_LINE}
          value={pendingIssue.title}
          onChange={(value) => this.changeFormValue(value, fieldName)}
          disabled={disabled || currentUserLoading}
          className="IssueCreateForm__text-input IssueCreateForm__field-width"
          data-testid="IssueCreateForm__title"
        />
      </FormField>
    );
  }

  renderRfiCustomIdentifier() {
    if (!this.state.isRfiCustomIdentifierVisible) {
      return null;
    }

    const { pendingIssue, disabled, container } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER;
    const currentUserLoading = this.getIsCurrentUserLoading();

    return (
      <RfiCustomIdentifier
        disabled={disabled || currentUserLoading}
        value={pendingIssue.customIdentifier}
        onChange={this.changeRfiCustomIdentifierFormValue}
        forceClientValidation={this.state.forceRfiCustomIdentifierValidation}
        onValidationChange={this.changeRfiCustomIdentifierFormValidationValue}
        populateRfiCustomIdentifierOnMount={this.state.populateRfiCustomIdentifierOnMount}
        className="IssueCreateForm__text-input IssueCreateForm__custom-identifier-field-width"
        key={fieldName}
        setFieldRef={this.setFieldRef(fieldName)}
        container={container}
      />
    );
  }

  renderDescription() {
    const { pendingIssue, disabled } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.DESCRIPTION;
    const currentUserLoading = this.getIsCurrentUserLoading();
    const textLabel = T.translate('new.question');
    const showErrorMessage = this.shouldShowFieldError(fieldName);
    const errorMessage = this.getFieldErrorMessage(fieldName);

    return (
      <FormField
        key={fieldName}
        setFieldRef={this.setFieldRef(fieldName)}
        errorMessageText={errorMessage}
        data-testid="IssueCreateForm__description"
        labelText={textLabel}
        showFieldError={showErrorMessage}
        isFieldMandatory={this.isFieldMandatory(fieldName)}
      >
        <TextFieldCore
          behavior={TextFieldCore.Behaviors.MULTI_LINE}
          value={pendingIssue.description}
          valid={!showErrorMessage}
          onChange={(value) => this.changeFormValue(value, fieldName)}
          disabled={disabled || currentUserLoading}
          className="IssueCreateForm__multi-line-text-field IssueCreateForm__field-width"
          data-testid="IssueCreateForm__description"
        />
      </FormField>
    );
  }

  renderSuggestedAnswer() {
    const { pendingIssue, disabled } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.SUGGESTED_ANSWER;

    const currentUserLoading = this.getIsCurrentUserLoading();
    const textLabelSuggestedAnswer = T.translate('new.suggestedAnswer');
    const showErrorMessage = this.shouldShowFieldError(fieldName);
    const errorMessage = this.getFieldErrorMessage(fieldName);

    return (
      <FormField
        key={fieldName}
        setFieldRef={this.setFieldRef(fieldName)}
        errorMessageText={errorMessage}
        data-testid="IssuesCreateForm__suggested-answer"
        labelText={textLabelSuggestedAnswer}
        showFieldError={showErrorMessage}
        isFieldMandatory={this.isFieldMandatory(fieldName)}
      >
        <TextFieldCore
          behavior={TextFieldCore.Behaviors.MULTI_LINE}
          value={pendingIssue.suggestedAnswer}
          valid={!showErrorMessage}
          onChange={(value) => this.changeFormValue(value, fieldName)}
          disabled={disabled || currentUserLoading}
          className="IssueCreateForm__multi-line-text-field IssueCreateForm__field-width"
          data-testid="IssueCreateForm__suggested-answer"
        />
      </FormField>
    );
  }

  renderAttachments() {
    const {
      disabled,
      container,
      isSubmitting,
      isUploadingAttachments,
      pendingIssueForm,
    } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.ATTACHMENTS_ATTRIBUTES;
    const showErrorMessage = pendingIssueForm.submitFailed && isUploadingAttachments;

    const currentUserLoading = this.getIsCurrentUserLoading();
    const textLabelAttachments = T.translate('new.attachments');

    return (
      <FormField
        key={fieldName}
        setFieldRef={this.setFieldRef(fieldName)}
        labelText={textLabelAttachments}
        errorMessageText={T.translate('new.error.attachmentUploading')}
        data-testid="IssuesCreateForm__attachments"
        showFieldError={showErrorMessage}
        isFieldMandatory={this.isFieldMandatory(fieldName)}
      >
        <IssueCreateFormAttachmentsSection
          disabled={disabled || currentUserLoading}
          uploadFiles={this.uploadFiles}
          chooseDocsDocument={this.chooseDocsDocument}
          onFileCancelClick={this.onFileCancelClick}
          canCancelFiles={!isSubmitting}
          container={container}
        />
      </FormField>
    );
  }

  renderLinkToDocument() {
    const {
      container,
      viewType,
      pendingIssue,
      formConfig = FORM_CONFIG_NAMES.CLASSIC,
    } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.TARGET_URN;

    const textLabelLinkToDocument = T.translate('new.linkToDocument');

    if (viewType === viewTypes.PROJECT) {
      return (
        <FormField
          key={fieldName}
          labelText={textLabelLinkToDocument}
          isFieldMandatory={this.isFieldMandatory(fieldName)}
          data-testid="IssueCreateForm__link-to-document"
        >
          <LinkToDocument
            container={container}
            status={pendingIssue.status}
            document={this.state.document}
            onSelectDocumentPushpin={this.handleSelectDocumentPushpin}
            onRemoveDocument={this.handleRemoveDocumentPushpin}
          />
        </FormField>
      );
    }
    return null;
  }

  renderLocationDescription() {
    const { pendingIssue, disabled } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.LOCATION_DESCRIPTION;
    const currentUserLoading = this.getIsCurrentUserLoading();
    const showErrorMessage = this.shouldShowFieldError(fieldName);
    const errorMessage = this.getFieldErrorMessage(fieldName);
    const textLabelLocation = T.translate('new.location');
    const textPlaceholderLocation = T.translate('new.placeholder.location');

    return (
      <FormField
        key={fieldName}
        setFieldRef={this.setFieldRef(fieldName)}
        labelText={textLabelLocation}
        showFieldError={showErrorMessage}
        data-testid="IssueCreateForm__location-description"
        errorMessageText={errorMessage}
        isFieldMandatory={this.isFieldMandatory(fieldName)}
      >
        <TextFieldCore
          behavior={TextFieldCore.Behaviors.SINGLE_LINE}
          value={pendingIssue.locationDescription}
          valid={!showErrorMessage}
          onChange={(value) => this.changeFormValue(value, fieldName)}
          placeholder={textPlaceholderLocation}
          disabled={disabled || currentUserLoading}
          className="IssueCreateForm__text-input IssueCreateForm__field-width"
          data-testid="IssueCreateForm__location-description"
        />
      </FormField>
    );
  }

  renderDueDate() {
    const { pendingIssue, disabled } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.DUE_DATE;
    const currentUserLoading = this.getIsCurrentUserLoading();
    const textLabelDueDate = T.translate('new.dueDate');
    const textPlaceholderDueDate = T.translate('new.placeholder.dueDate');
    const showErrorMessage = this.shouldShowFieldError(fieldName);
    const errorMessage = this.getFieldErrorMessage(fieldName);

    return (
      <FormField
        key={fieldName}
        setFieldRef={this.setFieldRef(fieldName)}
        labelText={textLabelDueDate}
        data-testid="IssueCreateForm__due-date"
        showFieldError={showErrorMessage}
        errorMessageText={errorMessage}
        isFieldMandatory={this.isFieldMandatory(fieldName)}
      >
        <DatePickerWithContainer
          disablePastDates
          valid={!showErrorMessage}
          date={pendingIssue.dueDate ? pendingIssue.dueDate : null}
          onChange={(value) => {
            this.changeFormValue(value, fieldName);
          }}
          className="IssueCreateForm__field-width"
          placeholder={textPlaceholderDueDate}
          disabled={disabled || currentUserLoading}
        />
      </FormField>
    );
  }

  renderCoReviewers() {
    const { disabled, users, container } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS;

    const textLabelCoReviewers = T.translate('new.coReviewers');
    const textPlaceholderCoReviewers = T.translate('new.placeholder.coReviewers');
    const textRfiCoReviewersInfo = T.translate('info.rfiCoReviewers');
    const currentUserLoading = this.getIsCurrentUserLoading();
    const showErrorMessage = this.shouldShowFieldError(fieldName);
    const errorMessage = this.getFieldErrorMessage(fieldName);

    return (
      <FormField
        key={fieldName}
        setFieldRef={this.setFieldRef(fieldName)}
        labelText={textLabelCoReviewers}
        showFieldError={showErrorMessage}
        errorMessageText={errorMessage}
        data-testid="IssueCreateForm__coReviewers"
        infoPopoverText={textRfiCoReviewersInfo}
        container={container}
        isFieldMandatory={this.isFieldMandatory(fieldName)}
      >
        <SelectDropdownWithContainer
          options={users}
          multi
          valid={!showErrorMessage}
          labelKey="name"
          valueKey="oxygenId"
          subtextGetter={getUserCompanyName}
          placeholder={textPlaceholderCoReviewers}
          className="IssueCreateForm__field-width"
          disabled={disabled || currentUserLoading}
          onChange={(value) => this.changeFormValue(value, fieldName)}
          clearable
          searchable
          searchIcon={<FilterSearchIcon />}
          searchPlaceholder={T.translate('new.search')}
        />
      </FormField>
    );
  }

  renderDistributionList() {
    const { disabled, users, container } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.DISTRIBUTION_LIST;

    const currentUserLoading = this.getIsCurrentUserLoading();

    const textLabelDistributionList = T.translate('new.distributionList');
    const textPlaceholderDistributionList = T.translate('new.placeholder.distributionList');
    const textRfiCoDistributionListInfo = T.translate('info.rfiDistributionList');
    const showErrorMessage = this.shouldShowFieldError(fieldName);
    const errorMessage = this.getFieldErrorMessage(fieldName);

    return (
      <FormField
        key={fieldName}
        setFieldRef={this.setFieldRef(fieldName)}
        errorMessageText={errorMessage}
        labelText={textLabelDistributionList}
        showFieldError={showErrorMessage}
        data-testid="IssueCreateForm__distribution-list"
        infoPopoverText={textRfiCoDistributionListInfo}
        container={container}
        isFieldMandatory={this.isFieldMandatory(fieldName)}
      >
        <SelectDropdownWithContainer
          options={users}
          multi
          valid={!showErrorMessage}
          labelKey="name"
          valueKey="oxygenId"
          subtextGetter={getUserCompanyName}
          placeholder={textPlaceholderDistributionList}
          className="IssueCreateForm__field-width"
          disabled={disabled || currentUserLoading}
          onChange={(value) => this.changeFormValue(value, fieldName)}
          clearable
          searchable
          searchIcon={<FilterSearchIcon />}
          searchPlaceholder={T.translate('new.search')}
        />
      </FormField>
    );
  }

  renderSpecSection() {
    const { specSections } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.SPEC_SECTION;
    const errorMessage = this.getFieldErrorMessage(fieldName);
    const textLabelSpecSection = T.translate('new.specSection');
    const textPlaceholderSpecSection = T.translate('new.placeholder.specSection');
    const showErrorMessage = this.shouldShowFieldError(fieldName);
    return (
      <FormField
        key={fieldName}
        setFieldRef={this.setFieldRef(fieldName)}
        errorMessageText={errorMessage}
        data-testid="IssueCreateForm__assignedTo"
        labelText={textLabelSpecSection}
        showFieldError={showErrorMessage}
        isFieldMandatory={this.isFieldMandatory(fieldName)}
      >
        <SelectDropdownWithContainer
          options={specSections}
          selectedOptions={null}
          valid={!showErrorMessage}
          placeholder={textPlaceholderSpecSection}
          className="IssueCreateForm__field-width"
          onChange={(value) => this.handleSelectAssignee(value)}
          searchable
          searchIcon={<FilterSearchIcon />}
          searchPlaceholder={T.translate('new.search')}
        />
      </FormField>
    );
  }

  renderCostImpact(disabled = false) {
    const { impactOptions } = this.props;
    const { selectedTransition } = this.state;
    function children(inputProps) {
      return (
        <SelectDropdownWithContainer
          {...inputProps}
          options={impactOptions}
          clearable
          className="IssueCreateForm__text-input IssueCreateForm__custom-identifier-field-width"
          disabled={disabled}
        />
      );
    }
    return withFormField({
      children,
      fieldName: ISSUE_ATTRIBUTE_NAMES.COST,
      selectedTransition,
      onChange: this.changeFormValue,
    });
  }

  renderScheduleImpact(disabled = false) {
    const { impactOptions } = this.props;
    const { selectedTransition } = this.state;

    function children(inputProps) {
      return (
        <SelectDropdownWithContainer
          {...inputProps}
          options={impactOptions}
          clearable
          className="IssueCreateForm__text-input IssueCreateForm__custom-identifier-field-width"
          disabled={disabled}
        />
      );
    }
    return withFormField({
      children,
      fieldName: ISSUE_ATTRIBUTE_NAMES.SCHEDULE,
      selectedTransition,
      onChange: this.changeFormValue,
    });
  }

  renderPriority(disabled = false) {
    const { selectedTransition } = this.state;
    const { priorityOptions, pendingIssue, initialFields } = this.props;
    const selectedOption =
      (pendingIssue && pendingIssue.priority) || (initialFields && initialFields.priority) || null;
    const selectedOptions = selectedOption ? toOptionsArray([selectedOption])[0] : null;

    function children(inputProps) {
      return (
        <SelectDropdownWithContainer
          {...inputProps}
          options={priorityOptions}
          selectedOptions={selectedOptions}
          disabled={disabled}
          clearable
          className="IssueCreateForm__text-input IssueCreateForm__custom-identifier-field-width"
        />
      );
    }
    return withFormField({
      children,
      fieldName: ISSUE_ATTRIBUTE_NAMES.PRIORITY,
      selectedTransition,
      onChange: this.changeFormValue,
    });
  }

  renderDiscipline(disabled = false) {
    const { pendingIssue, disciplineOptions } = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.DISCIPLINE;
    const children = (inputProps) => {
      return (
        <SelectDropdownWithContainer
          options={disciplineOptions}
          selectedOptions={disciplineOptions.filter(
            (el) => pendingIssue[fieldName] && pendingIssue[fieldName].includes(el.key)
          )}
          className="IssueCreateForm__field-width"
          disabled={disabled}
          clearable
          multi
          {...inputProps}
        />
      );
    };
    return withFormField({ children, fieldName, onChange: this.changeFormValue });
  }

  renderCategory(disabled = false) {
    const { pendingIssue, categoryOptions } = this.props;
    const { selectedTransition } = this.state;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.CATEGORY;

    const children = (inputProps) => {
      return (
        <SelectDropdownWithContainer
          options={categoryOptions}
          selectedOptions={categoryOptions.filter(
            (el) => pendingIssue[fieldName] && pendingIssue[fieldName].includes(el.key)
          )}
          className="IssueCreateForm__field-width"
          data-testid="IssueCreateForm__categoryField"
          clearable
          multi
          disabled={disabled}
          {...inputProps}
        />
      );
    };
    return withFormField({
      children,
      selectedTransition,
      fieldName,
      onChange: this.changeFormValue,
    });
  }

  renderSubId(disabled = false) {
    const { pendingIssue } = this.props;
    const { selectedTransition } = this.state;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.SUB_ID;
    const children = (inputProps) => {
      return (
        <TextFieldCore
          ref={(input) => {
            this.subIdInput = input;
          }}
          behavior={TextFieldCore.Behaviors.SINGLE_LINE}
          className="IssueCreateForm__text-input IssueCreateForm__field-width"
          data-testid="IssueCreateForm__subId"
          value={pendingIssue[fieldName]}
          maxLength={20}
          disabled={disabled}
          {...inputProps}
        />
      );
    };
    return withFormField({
      children,
      selectedTransition,
      fieldName,
      onChange: this.changeFormValue,
    });
  }

  getIsCurrentUserLoading() {
    const { currentUser } = this.props;
    return !currentUser || !currentUser.attributes;
  }

  renderField(fieldName) {
    const disabled = this.props.disabled || this.getIsCurrentUserLoading();
    switch (fieldName) {
      case ISSUE_ATTRIBUTE_NAMES.STATUS:
        return this.renderStatus();
      case ISSUE_ATTRIBUTE_NAMES.TITLE:
        return this.renderTitle();
      case ISSUE_ATTRIBUTE_NAMES.SPEC_SECTION:
        return this.renderSpecSection();
      case ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER:
        return this.renderRfiCustomIdentifier(disabled);
      case ISSUE_ATTRIBUTE_NAMES.DESCRIPTION:
        return this.renderDescription(disabled);
      case ISSUE_ATTRIBUTE_NAMES.SUGGESTED_ANSWER:
        return this.renderSuggestedAnswer();
      case ISSUE_ATTRIBUTE_NAMES.ATTACHMENTS_ATTRIBUTES:
        return this.renderAttachments();
      case ISSUE_ATTRIBUTE_NAMES.TARGET_URN:
        return this.renderLinkToDocument();
      case ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO:
        return this.renderAssignTo();
      case ISSUE_ATTRIBUTE_NAMES.LOCATION_DESCRIPTION:
        return this.renderLocationDescription();
      case ISSUE_ATTRIBUTE_NAMES.DUE_DATE:
        return this.renderDueDate();
      case ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS:
        return this.renderCoReviewers();
      case ISSUE_ATTRIBUTE_NAMES.DISTRIBUTION_LIST:
        return this.renderDistributionList();
      case ISSUE_ATTRIBUTE_NAMES.COST:
        return this.renderCostImpact(disabled);
      case ISSUE_ATTRIBUTE_NAMES.SCHEDULE:
        return this.renderScheduleImpact(disabled);
      case ISSUE_ATTRIBUTE_NAMES.PRIORITY:
        return this.renderPriority(disabled);
      case ISSUE_ATTRIBUTE_NAMES.DISCIPLINE:
        return this.renderDiscipline(disabled);
      case ISSUE_ATTRIBUTE_NAMES.CATEGORY:
        return this.renderCategory(disabled);
      case ISSUE_ATTRIBUTE_NAMES.SUB_ID:
        return this.renderSubId(disabled);
      default:
        return null;
    }
  }

  renderSeparator(key) {
    return <div key={key} className="IssueCreateForm__separator" />;
  }

  renderCreateComponent(fieldName, index) {
    switch (fieldName) {
      case CREATE_FORM_SEPARATOR:
        return this.renderSeparator(index);
      default:
        return this.renderField(fieldName);
    }
  }

  renderAttributesGroups(group, index) {
    const title = T.translate(`new.group.${lodashCamelCase(group.name)}`);
    return (
      <React.Fragment>
        {index ? this.renderSeparator(group.name) : null}
        <FormSection title={title} viewType={this.props.viewType}>
          {group.attributes.map(this.renderCreateComponent)}
        </FormSection>
      </React.Fragment>
    );
  }

  renderForm() {
    const {
      disabled,
      isSubmitting,
      currentUser,
      formConfig = FORM_CONFIG_NAMES.CLASSIC,
    } = this.props;
    const { formReady } = this.state;
    const enableFileUpload = !disabled && !isSubmitting;

    if (!currentUser || !formReady) {
      return <Loader />;
    }

    return (
      <form onSubmit={this.handleSubmit} className="IssueCreateForm__form">
        <Dropzone onDrop={this.uploadFiles} accept={getAllowedFiles()} disabled={!enableFileUpload}>
          <div className="IssueCreateForm__form-content-container" ref={this.handleScrollableRef}>
            <div className="IssueCreateForm__form-content">
              {FORM_CONFIG[formConfig].map(this.renderAttributesGroups)}
            </div>
          </div>
        </Dropzone>
      </form>
    );
  }

  renderNoWorkflowConfigurationPopup() {
    return (
      <AnimateHeightPopUp isOpen={this.state.showNoWorkflowConfiguration}>
        <div className="IssueCreateForm__popup">
          <IssuePreviewNoWorkflowConfiguration
            closePopup={() => {
              this.handleSelectStatusTransition(this.state.prevTransition);
              this.setState({ showNoWorkflowConfiguration: false });
            }}
          />
        </div>
      </AnimateHeightPopUp>
    );
  }

  applyInitialFieldsData() {
    const { initialFields, projectId, uploadAttachmentFiles } = this.props;
    if (initialFields) {
      // Fields
      const { assignedTo, ...restInitialFields } = initialFields;
      if (assignedTo) {
        const assignedToOptions = this.getAssignToOptions();
        const isInitialUserValid = assignedToOptions.some(({ id }) => id === assignedTo);

        if (isInitialUserValid) {
          this.changeFormValue(assignedTo, 'assignedTo');
        }
      }

      Object.keys(restInitialFields).forEach((field) => {
        const initialValue = restInitialFields[field];
        if (initialValue) {
          this.changeFormValue(initialValue, field);
        }
      });

      // Linked Document + pushpin
      if (initialFields.linkedDocument) {
        const { urn, pushpinAttributes } = initialFields.linkedDocument;
        const { currentSheet } = initialFields;
        if (urn) {
          docsSDKWrapper.docsSDK.getDocumentsByURNs(projectId, [urn]).then((document) => {
            if (document[0]) {
              this.setState({
                document: document[0],
                targetUrn: document[0].urn,
                issuePushpin: pushpinAttributes,
                currentSheet: currentSheet || null
              });
            }
          });
        }
      }

      // Attachments
      if (initialFields.attachmentsAttributes) {
        const { attachmentsAttributes } = initialFields;
        // DM
        const dmUrns = attachmentsAttributes
          .filter(({ urnType }) => urnType === DM_URN_TYPE)
          .map(({ urn }) => urn);
        docsSDKWrapper.docsSDK.getDocumentsByURNs(projectId, dmUrns).then((documents) => {
          documents.forEach((document) => this.chooseDocsDocument(document.urn, document));
        });

        // OSS
        const ossFileWrappers = attachmentsAttributes
          .filter(({ urnType }) => urnType === OSS_URN_TYPE)
          .map((fileWrapper) => ({ ...fileWrapper, id: getUUID() }));
        uploadAttachmentFiles(ossFileWrappers, null, false, attachmentOrigin.CREATE_FORM);
      }
    }
  }

  render() {
    const contentClasses = classNames('IssueCreateForm__content', {
      'IssueCreateForm__content--shadowed': this.state.showNoWorkflowConfiguration,
    });
    return (
      <StickyScrollableContainer className="IssueCreateForm">
        <StickyScrollableContainer.Header>
          {this.renderNoWorkflowConfigurationPopup()}
        </StickyScrollableContainer.Header>
        <StickyScrollableContainer.Content className={contentClasses}>
          {this.renderForm()}
        </StickyScrollableContainer.Content>
      </StickyScrollableContainer>
    );
  }
}

IssueCreateForm.displayName = 'IssueCreateForm';

IssueCreateForm.propTypes = {
  disabled: PropTypes.bool.isRequired,
  createAnother: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onSaveStart: PropTypes.func,
  onSaveSuccess: PropTypes.func,
  onSaveError: PropTypes.func,
  resetFormAction: PropTypes.func,
  createIssueAction: PropTypes.func.isRequired,
  changeFormValueAction: PropTypes.func.isRequired,
  setErrorsFormAction: PropTypes.func.isRequired,
  setSubmitFailedFormAction: PropTypes.func.isRequired,
  uploadAttachmentFiles: PropTypes.func.isRequired,
  createAttachmentForCreate: PropTypes.func.isRequired,
  cancelAttachmentUpload: PropTypes.func.isRequired,
  createVirtualFolder: PropTypes.func.isRequired,
  oxygen: oxygenType.isRequired,
  currentUser: userType,
  pendingIssue: pendingIssueAttributesType,
  pendingIssueForm: pendingIssueFormAttributesType.isRequired,
  pendingIssueFormAttributes: PropTypes.object.isRequired,
  issuePushpin: activePushpinAttributesType,
  document: documentType,
  targetUrn: PropTypes.string,
  origin: PropTypes.string,
  currentSheet: sheetObjectType,
  focusForm: PropTypes.bool,
  users: PropTypes.array,
  groupedProjectEntities: PropTypes.array,
  uploadingAttachments: PropTypes.array,
  isUploadingAttachments: PropTypes.bool,
  container: PropTypes.object,
  viewType: PropTypes.string.isRequired,
  trackEvent: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  clearUploadingAttachmentsListByOrigin: PropTypes.func.isRequired,
  isCreateFormDeepLinking: PropTypes.bool.isRequired,
  getRfiCustomIdentifierAction: PropTypes.func.isRequired,
  rfiCustomIdentifierAnalyticsData: PropTypes.object.isRequired,
  specSections: PropTypes.array.isRequired,
  impactOptions: PropTypes.array.isRequired,
  disciplineOptions: PropTypes.array.isRequired,
  categoryOptions: PropTypes.array.isRequired,
  priorityOptions: PropTypes.array.isRequired,
  formConfig: PropTypes.oneOf(Object.values(FORM_CONFIG_NAMES)),
  initialFields: PropTypes.shape({
    title: PropTypes.string,
    assignedTo: PropTypes.string,
    dueDate: PropTypes.instanceOf(Date),
    description: PropTypes.string,
    suggestedAnswer: PropTypes.string,
    locationDescription: PropTypes.string,
    priority: PropTypes.oneOf(RfiEnums.priority),
    attachmentsAttributes: PropTypes.array,
    linkedDocument: PropTypes.shape({
      urn: PropTypes.string.isRequired,
      pushpinAttributes: PropTypes.shape({
        externalId: PropTypes.string.isRequired,
        objectId: PropTypes.number.isRequired,
        attributesVersion: PropTypes.number.isRequired,
        position: PropTypes.shape({
          x: PropTypes.number.isRequired,
          y: PropTypes.number.isRequired,
          z: PropTypes.number.isRequired,
        }).isRequired,
      }).isRequired,
    }),
    currentSheet: PropTypes.shape({
      is3D: PropTypes.bool.isRequired,
      sheetGuid: PropTypes.string.isRequired,
      sheetName: PropTypes.string.isRequired,
    }),
    attachmentsAttributes: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        urn: PropTypes.string.isRequired,
        urnType: PropTypes.oneOf([OSS_URN_TYPE, DM_URN_TYPE]).isRequired
      })
    ),
  }),
};

const mapStateToProps = (state, ownProps) => {
  return {
    pendingIssue: getPendingIssue(state),
    pendingIssueForm: getPendingIssueForm(state),
    pendingIssueFormAttributes: getPendingIssueFormAttributes(state),
    oxygen: getOxygenMappings(state),
    currentUser: getCurrentUser(state),
    disabled: ownProps.disabled,
    focusForm: ownProps.focusForm,
    error: ownProps.error,
    onSaveStart: ownProps.onSaveStart,
    onSaveSuccess: ownProps.onSaveSuccess,
    onSaveError: ownProps.onSaveError,
    issuePushpin: ownProps.issuePushpin,
    document: ownProps.document,
    targetUrn: ownProps.targetUrn,
    currentSheet: getCurrentSheets(state).length ? getCurrentSheets(state)[0] : null,
    users: getActiveUsers(state),
    groupedProjectEntities: getGroupedProjectActiveEntities(state),
    uploadingAttachments: getUploadingAttachments(state),
    isUploadingAttachments: isUploadingAttachments(state, attachmentOrigin.CREATE_FORM),
    viewType: getCurrentViewType(state),
    isCreateFormDeepLinking: getIsCreateFormDeepLinking(state),
    rfiCustomIdentifierAnalyticsData: rfiCustomIdentifierAnalyticsData(state),
    specSections: rfiSpecSection(state),
    impactOptions: toOptionsArray(getImpacts(state)),
    categoryOptions: toOptionsArray(getRFIEnum('category')(state)),
    disciplineOptions: toOptionsArray(getRFIEnum('discipline')(state)),
    priorityOptions: toOptionsArray(getRFIEnum('priority')(state)),
    projectId: getProjectId(state),
  };
};

const {
  change: changeFormValueAction,
  setErrors: setErrorsFormAction,
  focus: focusFormAction,
  blur: blurFormAction,
  reset: resetFormAction,
  setSubmitFailed: setSubmitFailedFormAction,
} = formActions;

const mapDispatchToProps = {
  createIssueAction,
  changeFormValueAction,
  setErrorsFormAction,
  setSubmitFailedFormAction,
  focusFormAction,
  trackEvent,
  blurFormAction,
  resetFormAction,
  uploadAttachmentFiles: AttachmentUploadThunk.uploadAttachmentFiles,
  createAttachmentForCreate,
  clearUploadingAttachmentsListByOrigin,
  cancelAttachmentUpload,
  getRfiCustomIdentifierAction,
  createVirtualFolder: createVirtualFolderAction,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  IssueCreateForm
);
